//Principais
import { Component, DestroyRef, HostListener, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgSelectConfig } from '@ng-select/ng-select';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { distinctUntilChanged, lastValueFrom, map, skip } from "rxjs";

import { AuthService } from 'src/app/shared/auth/services/auth.service';
import { AtribuicaoLoginSed, UserLoginSed } from 'src/app/shared/models/sed/UserLogin';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { Diretoria } from 'src/app/shared/models/diretoria/Diretoria';
import { Escola } from 'src/app/shared/models/escola/Escola';
import { EscolaService } from 'src/app/shared/services/escola.service';
import { PlanoMelhoria } from 'src/app/shared/models/plano-melhoria/planoMelhoria';
import { PlanoMelhoriaValidacao } from 'src/app/shared/models/plano-melhoria/planoMelhoriaValidacao';
import { PlanoMelhoriaService } from 'src/app/shared/services/plano-melhoria.service';
import { PlanoMelhoriaCadastroComponent } from 'src/app/pages/plano-melhoria/adicionar/plano-melhoria-cadastro.component';
import { Perfil } from 'src/app/shared/enums/Perfis/Perfis';
import { ActivatedRoute, Router } from '@angular/router';
import { StatusPlanoMelhoria } from 'src/app/shared/enums/PlanoMelhoria/StatusPlanoMelhoria';

@Component({
  selector: 'app-plano-melhoria-consulta',
  templateUrl: './plano-melhoria-consulta.component.html',
  styleUrls: ['./plano-melhoria-consulta.component.scss'],
  standalone: true,
  imports: [angularCommonImports, libsCommonImports, convivaCommonImports, PlanoMelhoriaCadastroComponent]
})
export class PlanoMelhoriaConsultaComponent implements OnInit {
  //Injeções
  private escolaService = inject(EscolaService);
  private config = inject(NgSelectConfig);
  private spinnerService = inject(NgxSpinnerService);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);
  private ConvivaErrors = inject(ConvivaErrorHandlingService);
  private planoMelhoriaService = inject(PlanoMelhoriaService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  /** Filtro opcional vindo do pop-up (?validacao=supervisor|pec). */
  private filtroValidacaoPendente: 'supervisor' | 'pec' | null = null;

  public userLogado: UserLoginSed | null = null;
  public atribuicoesLogin?: AtribuicaoLoginSed;
  public showCadastroModal = false;
  public showAprovarModal = false;
  public showReprovarModal = false;
  public qtdeCaracteresJustificativa: number = 0;
  public planoSelecionado: PlanoMelhoria | null = null;
  public lstPlanosMelhoria: PlanoMelhoria[] = [];
  public podeQualificarPlano: boolean = false;
  public legendaEvidenciasGrid = `
    <div class="legenda-evidencias" aria-label="Legenda de evidências">
      <strong>Legenda - Evidências:</strong>
      <span><span class="bolinha-evidencia nenhuma"></span>Nenhuma</span>
      <span><span class="bolinha-evidencia parcial"></span>Parcial</span>
      <span><span class="bolinha-evidencia completo"></span>Completo</span>
    </div>
  `;
  public colunasGrid = [
    { field: 'codigoPlanoMelhoria', headerSort: false, visible: false },
    {
      title: 'Ano',
      field: 'anoLetivo',
      width: 80,
      hozAlign: "center",
      headerHozAlign: "center",
      headerFilter: 'input'
    },
    {
      title: 'Escola',
      field: 'nomeEscola',
      width: 180,
      hozAlign: "left",
      headerHozAlign: "center",
      headerFilter: 'input',
      formatter: (cell: any) => {
        const val = cell.getValue();
        return val != null && val !== '' ? val : '—';
      }
    },
    {
      title: 'Status',
      field: 'statusDescricao',
      minWidth: 220,
      hozAlign: "center",
      headerHozAlign: "center",
      headerFilter: 'list',
      headerFilterParams: {
        values: {
          "": "Todos",
          "Novo": "Novo",
          "Aguardando Validação Supervisor de Ensino": "Aguardando Validação Supervisor de Ensino",
          "Aguardando Validação PEC CONVIVA": "Aguardando Validação PEC CONVIVA",
          "Aprovado": "Aprovado",
          "Devolvido para ajustes": "Devolvido para ajustes"
        }
      },
      formatter: (cell: any) => {
        const data = cell.getRow().getData();
        const status = data.status;

        let cor = '#999';

        switch (status) {
          case 0: // Novo
            cor = '#006ebd';
            break;
          case 1: // Aguardando Validação Supervisor de Ensino
          case 2: // Aguardando Validação PEC CONVIVA
            cor = '#f57e3a';
            break;
          case 3: // Devolvido para ajustes
            cor = '#cb2525';
            break;
          case 4: // Aprovado
            cor = '#1fce7c';
            break;
        }

        const texto = cell.getValue() || '';
        return `
          <div style="display: inline-flex; align-items: center; gap: 8px; justify-content: center;">
            <span style="width: 10px; height: 10px; border-radius: 50%; background-color: ${cor}; display: inline-block; flex-shrink: 0;"></span>
            <span style="font-size: 1.2rem;">${texto}</span>
          </div>
        `;
      }
    },
    {
      title: 'Evidências',
      field: 'statusEvidencias',
      minWidth: 120,
      hozAlign: "center",
      headerHozAlign: "center",
      headerSort: false,
      formatter: (cell: any) => this.formatarStatusEvidencias(cell.getRow().getData())
    },
    {
      title: 'Data de registro',
      field: 'dtInclusaoFormatada',
      width: 120,
      hozAlign: "center",
      headerHozAlign: "center",
      headerFilter: 'input',
      formatter: (cell: any) => {
        const val = cell.getValue();
        if (val == null) return '—';
        const d = new Date(val);
        return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString('pt-BR');
      }
    },
    {
      title: 'Ações',
      field: 'acoes',
      width: 150,
      hozAlign: "center",
      headerHozAlign: "center",
      headerSort: false,
      cssClass: "col-acoes",
      formatter: (cell: any) => {
        const data = cell.getRow().getData();
        const status = data.status;
        const podeAprovarReprovar = (status === 1 && this.userLogado?.codigoPerfil === Perfil.SupervisorEducacional)
          || (status === 2 && this.userLogado?.codigoPerfil === Perfil.PEC);
        const podeEnviarValidacao = (status === 0 || status === 3);

        let html = `<div style="display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: nowrap;">`;
        html += `<i class="fa-solid fa-eye" title="Visualizar" style="cursor: pointer;"></i>`;
        if (podeEnviarValidacao) {
          html += `<i class="fa-solid fa-arrows-rotate" title="Enviar para Validação" style="cursor: pointer;"></i>`;
        }
        if (podeAprovarReprovar) {
          html += `<i class="fa-solid fa-check-square" title="Aprovar" style="cursor: pointer;"></i>`;
          html += `<i class="fa-solid fa-times" title="Devolver para ajustes" style="cursor: pointer;"></i>`;
        }
        html += `</div>`;
        return html;
      },
      cellClick: (e: any, cell: any) => this.OnClickAcao(e, cell)
    }
  ];

  constructor() {
    this.config.notFoundText = "Não encontrado";

    this.userLogado = this.authService.getUser();
    this.atribuicoesLogin = this.userLogado?.DadosUsuario?.[0];
    this.podeQualificarPlano = this.atribuicoesLogin?.NumeroComportamento === 1;

  }

  private obterResumoEvidencias(plano: PlanoMelhoria): { totalAcoes: number; totalAcoesComArquivo: number; cor: string; valor: string; texto: string } {
    const totalAcoes = Number(plano.quantidadeAcoes ?? 0);
    const totalAcoesComArquivo = Number(plano.quantidadeAcoesComArquivo ?? 0);

    let cor = '#b8b8b8';
    if (totalAcoesComArquivo > 0 && totalAcoesComArquivo < totalAcoes) {
      cor = '#f57e3a';
    } else if (totalAcoes > 0 && totalAcoesComArquivo >= totalAcoes) {
      cor = '#1fce7c';
    }

    return {
      totalAcoes,
      totalAcoesComArquivo,
      cor,
      valor: `${totalAcoesComArquivo}/${totalAcoes} ações`,
      texto: `${totalAcoesComArquivo} ações com evidência de ${totalAcoes} ações`
    };
  }

  private formatarStatusEvidencias(plano: PlanoMelhoria): string {
    const resumo = this.obterResumoEvidencias(plano);
    return `
      <div title="${resumo.texto}" style="display: inline-flex; align-items: center; gap: 8px; justify-content: center;">
        <span style="width: 10px; height: 10px; border-radius: 50%; background-color: ${resumo.cor}; display: inline-block; flex-shrink: 0;"></span>
        <span style="font-size: 1.2rem;">${resumo.valor}</span>
      </div>
    `;
  }

  private prepararPlanosGrid(planos: PlanoMelhoria[]): PlanoMelhoria[] {
    return planos.map((plano) => {
      const resumo = this.obterResumoEvidencias(plano);
      return {
        ...plano,
        statusEvidencias: resumo.valor
      };
    });
  }

  public OnPlanoEvidenciasAtualizado(planoAtualizado: PlanoMelhoria): void {
    const [planoGrid] = this.prepararPlanosGrid([planoAtualizado]);
    this.lstPlanosMelhoria = this.lstPlanosMelhoria.map((plano) =>
      Number(plano.codigoPlanoMelhoria) === Number(planoGrid.codigoPlanoMelhoria)
        ? planoGrid
        : plano
    );
    this.planoSelecionado = planoGrid;
    this.atualizarPlanosExibicao();
  }

  @ViewChild('modalContent') modalContent!: TemplateRef<any>;

  public submitted = false;

  public diretorias: Diretoria[] = [];
  public escolas: Escola[] = [];

  public formCadastrar!: FormGroup;
  public formModal!: FormGroup;

  @Input() tituloAcaoModal: string = '';
  @Input() statusPlano?: number;

  public get form() {
    return this.formCadastrar.controls;
  }

  public get modalForm() {
    return this.formModal.controls;
  }

  // DADOS GERAIS
  public get codigoDiretoria() {
    return this.formCadastrar.get('codigoDiretoria')!;
  }
  public get codigoEscola() {
    return this.formCadastrar.get('codigoEscola')!;
  }
  public get dsJustificativa() {
    return this.formModal.get('dsJustificativa')!;
  }

  /** Dados passados ao Tabulator — referência estável (evita loop: getter + filter gerava array novo a cada CD). */
  public planosMelhoriaExibicao: PlanoMelhoria[] = [];

  private atualizarPlanosExibicao(): void {
    if (!this.filtroValidacaoPendente) {
      this.planosMelhoriaExibicao = this.lstPlanosMelhoria;
      return;
    }
    const st =
      this.filtroValidacaoPendente === 'supervisor'
        ? StatusPlanoMelhoria.AguardandoValidacaoSupervisor
        : StatusPlanoMelhoria.AguardandoValidacaoPecConviva;
    /** Reforço: lista já vem da API por status (igual à contagem do pop-up); Number evita mismatch string/número. */
    this.planosMelhoriaExibicao = this.lstPlanosMelhoria.filter((p) => Number(p.status) === st);
  }

  private sincronizarFiltroValidacaoQuery(v: string | null): void {
    if (v === 'supervisor' || v === 'pec') {
      this.filtroValidacaoPendente = v;
    } else {
      this.filtroValidacaoPendente = null;
    }
  }

  public async ngOnInit() {
    this.formCadastrar = new FormGroup({
      codigoDiretoria: new FormControl(null, [Validators.required]),
      codigoEscola: new FormControl(null),
    });

    this.formModal = new FormGroup({
      dsJustificativa: new FormControl(null, [Validators.required, Validators.minLength(150), Validators.maxLength(1000)]),
    });

    this.sincronizarFiltroValidacaoQuery(this.route.snapshot.queryParamMap.get('validacao'));

    this.route.queryParamMap
      .pipe(
        map((p) => p.get('validacao')),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((v) => {
        this.sincronizarFiltroValidacaoQuery(v);
        const cdDe = this.formCadastrar.get('codigoDiretoria')?.value;
        if (cdDe != null && cdDe !== '') {
          void this.PesquisarPlanos();
        } else {
          this.atualizarPlanosExibicao();
        }
      });

    this.spinnerService.show();

    const numeroComportamento = this.atribuicoesLogin?.NumeroComportamento;
    if (numeroComportamento == null) {
      this.toastr.error('Usuário não autenticado. Faça login novamente.');
      this.spinnerService.hide();
      return;
    }

    if (numeroComportamento == 1) {
      await this.getDiretorias();
    }

    if (numeroComportamento == 2) {
      this.getDiretoriasPerfilDiretoria();
    }

    if (numeroComportamento == 3 || numeroComportamento == 4) {
      this.getDiretoriasPerfilEscola();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.spinnerService.hide();
    }
  }

  public async PesquisarPlanos() {
    const cdDe = this.codigoDiretoria?.value;
    const cdEscola = this.codigoEscola?.value;

    if (cdDe == null || cdDe === '') {
      this.atualizarPlanosExibicao();
      this.spinnerService.hide();
      return;
    }

    let result;

    if (this.filtroValidacaoPendente === 'supervisor') {
      /** Mesma consulta da contagem (GetContagemPendenciasValidacao — Supervisor): diretoria + status, sem escola. */
      result = await lastValueFrom(
        this.planoMelhoriaService.getPlanoMelhoriaPorDiretoriaEStatus(
          cdDe,
          StatusPlanoMelhoria.AguardandoValidacaoSupervisor
        )
      ).catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });
    } else if (this.filtroValidacaoPendente === 'pec') {
      /** Alinhado ao handler: escola informada → por escola; senão → por diretoria. */
      const escolaNum = typeof cdEscola === 'number' ? cdEscola : Number(cdEscola);
      if (escolaNum > 0) {
        result = await lastValueFrom(
          this.planoMelhoriaService.getPlanoMelhoriaPorEscolaEStatus(
            escolaNum,
            StatusPlanoMelhoria.AguardandoValidacaoPecConviva
          )
        ).catch((err) => {
          return this.ConvivaErrors.handleResultError(err);
        });
      } else {
        result = await lastValueFrom(
          this.planoMelhoriaService.getPlanoMelhoriaPorDiretoriaEStatus(
            cdDe,
            StatusPlanoMelhoria.AguardandoValidacaoPecConviva
          )
        ).catch((err) => {
          return this.ConvivaErrors.handleResultError(err);
        });
      }
    } else {
      const escolaApi =
        cdEscola != null && cdEscola !== '' && Number(cdEscola) > 0
          ? Number(cdEscola)
          : 0;
      result = await lastValueFrom(
        this.planoMelhoriaService.getPlanoMelhoriaPorDiretoriaEEscola(cdDe, escolaApi)
      ).catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });
    }

    if (!result.isSucess) {
      this.toastr.error(result.message, result.title);
      this.spinnerService.hide();
      return;
    }

    this.lstPlanosMelhoria = result.data ? this.prepararPlanosGrid(result.data) : [];
    this.atualizarPlanosExibicao();
    this.spinnerService.hide();
  }

  public async ConfirmarAprovacaoPlano() {
    if (!this.planoSelecionado)
      return;

    this.spinnerService.show();

    if (!this.userLogado || !this.atribuicoesLogin) {
      this.toastr.error('Usuário não autenticado. Faça login novamente.');
      this.spinnerService.hide();
      return;
    }

    let status = 0;

    if(this.userLogado?.codigoPerfil == Perfil.SupervisorEducacional)
      status = 2;
    else if(this.userLogado?.codigoPerfil == Perfil.PEC)
      status = 4;
    else{
      this.toastr.error('Você não tem permissão para aprovar este plano.');
      this.spinnerService.hide();
      return;
    }

    const payload: PlanoMelhoriaValidacao = {
      codigoPlanoMelhoria: this.planoSelecionado.codigoPlanoMelhoria,
      justificativa: '',
      codigoUsuario: this.atribuicoesLogin.CodigoUsuario,
      nomeUsuario: this.userLogado.nome,
      codigoPerfil: this.atribuicoesLogin.CodigoPerfil,
      nomePerfil: this.atribuicoesLogin.NomePerfil,
      status: status,
    };

    const result = await lastValueFrom(this.planoMelhoriaService.setAprovarPlanoMelhoria(payload))
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });

    if (!result.isSucess) {
      this.toastr.error(result.message, result.title);
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.hide();
    this.toastr.success('Plano de Melhoria de Convivência Escolar aprovado com sucesso.');

    this.FecharAprovarModal();
    this.PesquisarPlanos();
  }

  public async ConfirmarReprovacaoPlano() {
    if (!this.planoSelecionado)
      return;

    this.submitted = true;
    this.spinnerService.show();

    if (!this.userLogado || !this.atribuicoesLogin) {
      this.toastr.error('Usuário não autenticado. Faça login novamente.');
      this.spinnerService.hide();
      return;
    }

    if (this.formModal.invalid) {
      this.spinnerService.hide();
      return;
    }

    const payload: PlanoMelhoriaValidacao = {
      codigoPlanoMelhoria: this.planoSelecionado.codigoPlanoMelhoria,
      justificativa: this.dsJustificativa?.value,
      codigoUsuario: this.atribuicoesLogin.CodigoUsuario,
      nomeUsuario: this.userLogado.nome,
      codigoPerfil: this.atribuicoesLogin.CodigoPerfil,
      nomePerfil: this.atribuicoesLogin.NomePerfil,
    };

    const result = await lastValueFrom(this.planoMelhoriaService.setReprovarPlanoMelhoria(payload))
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });

    if (!result.isSucess) {
      this.toastr.error(result.message, result.title);
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.hide();
    this.toastr.success('Plano de Melhoria de Convivência Escolar reprovado com sucesso.');

    this.FecharReprovarModal();
    this.PesquisarPlanos();
  }

  private async OnClickAcao(e: any, cell: any) {
    const row = cell.getRow().getData();
    const target = e.target;

    if (target.classList.contains('fa-eye')) {
      this.planoSelecionado = row;
      this.showCadastroModal = true;
      this.tituloAcaoModal = 'Visualizar plano';
      this.statusPlano = row.status;
    }

    if (target.classList.contains('fa-check-square')) {
      this.planoSelecionado = row;
      this.showAprovarModal = true;
    }

    if (target.classList.contains('fa-close') || target.classList.contains('fa-times')) {
      this.planoSelecionado = row;
      this.showReprovarModal = true;
    }

    if (target.classList.contains('fa-arrows-rotate')) {
      this.planoSelecionado = row;
      await this.EnviarPlanoParaValidacao(row);
    }
  }

  public async EnviarPlanoParaValidacao(plano: PlanoMelhoria) {
    if (!plano?.codigoPlanoMelhoria) return;
    this.spinnerService.show();
    const result = await lastValueFrom(this.planoMelhoriaService.EnviarPlanoParaValidacao(plano))
      .catch((err) => this.ConvivaErrors.handleResultError(err));
    if (!result.isSucess) {
      this.toastr.error(result.message ?? 'Erro ao enviar para validação.');
      this.spinnerService.hide();
      return;
    }
    this.toastr.success('Plano enviado para validação com sucesso.');
    this.spinnerService.hide();
    await this.PesquisarPlanos();
  }

  public FecharCadastroModal() {
    this.showCadastroModal = false;
    this.planoSelecionado = null;
    this.tituloAcaoModal = '';
    this.statusPlano = undefined;
    // Refresh the grid data to show any changes made in the modal
    this.PesquisarPlanos();
  }

  public FecharAprovarModal() {
    this.showAprovarModal = false;
    this.planoSelecionado = null;
  }

  public FecharReprovarModal() {
    this.showReprovarModal = false;
    this.planoSelecionado = null;
    this.modalForm.dsJustificativa.setValue(null);
  }

  public async getDiretorias() {
    this.spinnerService.show();
    const result = await lastValueFrom(this.escolaService.getDiretoriaPorTipoDiretoria())
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });
    if (!result.isSucess) {
      this.toastr.error(result.message, result.title);
      this.spinnerService.hide();
      return;
    }
    this.diretorias = result.data;
    this.spinnerService.hide();
    if (this.userLogado?.codigoDiretoria != undefined) {
      this.ativaDiretoria();
      this.getEscolas(true);
    }
  }

  public getDiretoriasPerfilDiretoria() {
    this.diretorias = [];
    this.escolas = [];
    this.userLogado?.DadosUsuario?.map((item: AtribuicaoLoginSed) => {
      const diretoria = new Diretoria();
      diretoria.codigoDiretoria = item.CodigoDiretoria;
      diretoria.nomeDiretoria = item.NomeDiretoria;
      this.diretorias.push(diretoria);
    });

    this.spinnerService.hide();

    if (this.diretorias.length == 1) {
      this.form.codigoDiretoria.setValue(this.diretorias[0].codigoDiretoria);
      this.form.codigoDiretoria.disable();
      this.getEscolas(true);
    } else {
      this.ativaDiretoria();
      this.getEscolas(true);
    }
  }

  public getDiretoriasPerfilEscola() {

    this.diretorias = [];
    this.escolas = [];

    this.userLogado?.DadosUsuario?.map((item: AtribuicaoLoginSed) => {
      const diretoria = new Diretoria();
      diretoria.codigoDiretoria = item.CodigoDiretoria;
      diretoria.nomeDiretoria = item.NomeDiretoria;
      this.diretorias.push(diretoria);

      const escola = new Escola();
      escola.codigoEscola = item.CodigoEscola;
      escola.nomeEscola = item.NomeEscola;
      escola.codigoDiretoria = item.CodigoDiretoria;
      this.escolas.push(escola);
    });

    if (this.diretorias.length == 1 && this.escolas.length == 1) {
      this.form.codigoDiretoria.setValue(this.diretorias[0].codigoDiretoria);
      this.form.codigoDiretoria.disable();

      this.form.codigoEscola.setValue(this.escolas[0].codigoEscola);
      this.form.codigoEscola.disable();
    }

    if (this.diretorias.length == 1 && this.escolas.length > 1) {
      this.form.codigoDiretoria.setValue(this.diretorias[0].codigoDiretoria);
      this.form.codigoDiretoria.disable();
    }
    this.ativaDiretoria();
    this.ativaEscola();
    this.spinnerService.hide();
  }

  public async getEscolas(diretoriaLogada: boolean = false) {
    const codigoDE = this.userLogado?.codigoDiretoria != undefined && diretoriaLogada ? this.userLogado?.codigoDiretoria : this.formCadastrar.get('codigoDiretoria')?.value;
    const numeroComportamento = this.atribuicoesLogin?.NumeroComportamento;
    if (numeroComportamento == 1 || numeroComportamento == 2) {
      this.zerarEscola();
      if (codigoDE != null) {
        this.spinnerService.show();
        const result = await lastValueFrom(this.escolaService.getEscolasPorCodigoDiretoria(codigoDE))
          .catch((err) => {
            return this.ConvivaErrors.handleResultError(err);
          });
        if (!result.isSucess) {
          this.toastr.error(result.message, result.title);
          this.spinnerService.hide();
          return;
        }
        this.escolas = result.data;
        this.spinnerService.hide();
        this.ativaEscola();
      }
      this.spinnerService.hide();
    }
    if (this.userLogado !== null) {
      this.userLogado.codigoDiretoria = codigoDE == null ? 0 : codigoDE;
      this.authService.setUser(this.userLogado);
    }

    await this.PesquisarPlanos();
  }

  public zerarEscola() {
    this.escolas = [];
    this.formCadastrar.get('codigoEscola')?.patchValue(null);
  }

  public ativaEscola() {
    if (this.userLogado?.codigoEscola != undefined)
      this.formCadastrar.get('codigoEscola')?.setValue(this.userLogado?.codigoEscola);
    if (this.userLogado !== null)
      this.authService.setUser(this.userLogado);
  }

  public ativaDiretoria() {
    if (this.userLogado?.codigoDiretoria != undefined)
      this.formCadastrar.get('codigoDiretoria')?.setValue(this.userLogado?.codigoDiretoria);
    if (this.userLogado !== null)
      this.authService.setUser(this.userLogado);
  }

  public OnChangeCountLettersJustificativa() {
    const descricao = this.formModal.get('dsJustificativa')!.value;
    if (descricao != null)
      this.qtdeCaracteresJustificativa = descricao.length;
  }

  navigateToCadastrarPlano() {
    this.router.navigate(['/plano-melhoria/cadastro'], { queryParams: { openModal: 'true' } }).catch(err => console.error(err));
  }
}


